// Thin fetch wrapper. All requests share the session cookie via credentials.
const BASE = '/api';

// EVERY FAILURE LEAVES HERE CARRYING A CODE the Hebrew map recognises. That is the whole
// contract of this function, and it used to have one hole in it.
//
// `JSON.parse` was called on the raw body with nothing around it, so a body that is not
// JSON threw a SyntaxError — and its message is "Unexpected token '<', …", which is not a
// code, so i18n/errors.js could only fall back to the generic "אירעה שגיאה". The case is
// not hypothetical: in dev the API runs under nodemon and restarts on every save, and
// while it is down the Vite proxy answers with an HTML error page. So the one moment the
// user most needs to be told "the server is restarting" was the one moment the app said
// nothing useful at all.
//
// A non-JSON body now becomes `server_unreachable`. It is deliberately NOT gated on
// `res.ok`: a 200 carrying HTML means something is answering for the API that is not the
// API, which is the same fact from the caller's point of view.
const parse = async (res) => {
  const text = await res.text();
  let data = null;
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      throw new Error('server_unreachable');
    }
  }
  if (!res.ok) {
    // `res.statusText` is the last resort and is not a code either — but by this point the
    // body was valid JSON without an `error` field, which no endpoint here produces, so it
    // stays as a readable fallback rather than another invented code.
    const error = new Error((data && data.error) || res.statusText);
    // Some errors carry specifics the message needs to be readable — e.g. which
    // outlet still holds a clipping. The code stays in `message`; this fills blanks.
    if (data && data.details) error.details = data.details;
    throw error;
  }
  return data;
};

const send = (path, method, body) =>
  fetch(BASE + path, {
    method,
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: body ? JSON.stringify(body) : undefined,
  }).then(parse);

// Multipart POST for file uploads. The Content-Type header is deliberately NOT
// set: the browser must add it itself so it can append the multipart boundary,
// which is why this cannot go through `send` above.
export const postForm = (path, formData) =>
  fetch(BASE + path, { method: 'POST', credentials: 'include', body: formData }).then(parse);

// `no-store`: a read must always reach the server. The API sends no caching headers and
// the browser revalidates by ETag today, but a proxy in front of production that cached
// /api would show figures from before the last save with nothing on screen to say so.
export const get = (path) => fetch(BASE + path, { credentials: 'include', cache: 'no-store' }).then(parse);
export const post = (path, body) => send(path, 'POST', body);
export const put = (path, body) => send(path, 'PUT', body);
export const del = (path) => fetch(BASE + path, { method: 'DELETE', credentials: 'include' }).then(parse);
